import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { Box, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemTabs } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import { SystemBanner } from "../../../../components/UIToolkit/molecules/SystemBanner";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import type {
	ConfirmedShiftHours,
	EmployeeBucket,
	RecurringCalculationResult,
	RecurringCalculationSettings,
	RecurringCreationResult,
	ShiftHoursSlot,
} from "../../../../services/backend.service";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import { useApproveRecurringCandidates, useCalculateRecurringCandidates } from "../../hooks";
import { RecurringCandidatesTable } from "./RecurringCandidatesTable";
import { RecurringResultSummary } from "./RecurringResultSummary";
import { RecurringSettingsPanel } from "./RecurringSettingsPanel";
import { RecurringShiftHoursPanel } from "./RecurringShiftHoursPanel";
import { RecurringShiftHoursSaveDialog } from "./RecurringShiftHoursSaveDialog";
import { RecurringShiftHoursSaveResult } from "./RecurringShiftHoursSaveResult";
import { BUCKET_LABELS, bucketDescription, editedJobNames, summariseApprovalOutcomes } from "./recurringDisplay";
import { useRecurringShiftHoursSave } from "./useRecurringShiftHoursSave";

/**
 * DEFAULT: the score slider has to start somewhere, and the backend deliberately refuses to pick
 * (see `RecurringCalculationSettings.minimum_score` — it decides how many wrong shifts reach a real
 * schedule, so it is the operator's call). 0.8 is the design's own "about 1 in 5 generated shifts may
 * be wrong" band: strict enough that Ready means ready, loose enough that a normal department is not
 * entirely Needs review. It is a starting POSITION, not a floor — the whole 0.00–1.00 range stays
 * reachable, and the guidance under the slider updates as it moves.
 */
const DEFAULT_MINIMUM_SCORE = 0.8;

const INITIAL_SETTINGS: RecurringCalculationSettings = {
	minimumScore: DEFAULT_MINIMUM_SCORE,
	pattern: "auto",
	patternByJob: {},
	baseWindowMonths: 2,
	// No retry: the run answers from the window the operator chose, not a longer one.
	extendedWindowMonths: null,
	customCycleWeeks: null,
	shiftHours: [],
};

const BUCKET_ORDER: EmployeeBucket[] = ["ready", "needsReview", "skipped"];

const rowsForBucket = (result: RecurringCalculationResult, bucket: EmployeeBucket) =>
	bucket === "ready" ? result.ready : bucket === "needsReview" ? result.needsReview : result.skipped;

/**
 * "Calculate recurring" (DAT-774): propose one standing pattern per employee, review it, approve it.
 *
 * The flow is deliberately two calls, not one. Calculating writes nothing — it returns a proposal the
 * operator reads, corrects (the shift hours are the common correction) and approves per employee.
 * Only the approve call creates templates, so there is no run that silently puts a wrong pattern on
 * someone's schedule.
 */
export function CalculateRecurringSection() {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { showSnackbar } = useSnackbar();
	const departmentId = chosenDepartment?.department_id;

	const [settings, setSettings] = useState<RecurringCalculationSettings>(INITIAL_SETTINGS);
	const [result, setResult] = useState<RecurringCalculationResult | null>(null);
	const [shiftHoursEdits, setShiftHoursEdits] = useState<Record<string, ShiftHoursSlot[]>>({});
	const [selectedIds, setSelectedIds] = useState<Record<string, string[]>>({ ready: [], needsReview: [] });
	const [tab, setTab] = useState<EmployeeBucket>("ready");
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [creation, setCreation] = useState<RecurringCreationResult | null>(null);

	const { mutateAsync: calculate, isPending: calculating } = useCalculateRecurringCandidates();
	const { mutateAsync: approve, isPending: approving } = useApproveRecurringCandidates();
	const hoursSave = useRecurringShiftHoursSave({ result, setResult, edits: shiftHoursEdits });

	const jobNames = useMemo(() => result?.shiftHours.map((entry) => entry.jobName) ?? [], [result]);
	const approvedIds = useMemo(() => [...selectedIds.ready, ...selectedIds.needsReview], [selectedIds]);

	/** The settings a run should actually be sent, folding in any hours the operator corrected. */
	const settingsWithConfirmedHours = useCallback(
		(base: RecurringCalculationSettings): RecurringCalculationSettings => {
			const confirmed: ConfirmedShiftHours[] = Object.entries(shiftHoursEdits)
				.filter(([, slots]) => slots.length > 0)
				.map(([jobName, slots]) => ({ jobName, slots, source: "confirmed" as const }));
			return { ...base, shiftHours: confirmed };
		},
		[shiftHoursEdits],
	);

	const runCalculation = async (base: RecurringCalculationSettings) => {
		if (!departmentId) {
			showSnackbar?.("Please select a department", "error");
			return;
		}
		const sent = settingsWithConfirmedHours(base);
		const proposal = await calculate({ departmentId, settings: sent });
		setSettings(sent);
		setResult(proposal);
		hoursSave.resetForProposal(proposal, shiftHoursEdits, departmentId);
		setCreation(null);
		// Ready is the low-risk bucket, so it starts kept; Needs review starts empty precisely because
		// each of those rows is asking the reviewer a question that a pre-tick would answer for them.
		setSelectedIds({ ready: proposal.ready.map((row) => row.candidateId), needsReview: [] });
		setTab("ready");
	};

	/**
	 * Hours the operator corrected but has not recalculated with.
	 *
	 * Approval is blocked while any are outstanding rather than folding them into the payload. The
	 * proposal on screen — every pattern, score and shift time in it — was fitted against the OLD
	 * hours, so sending the corrections would persist templates whose hours nobody ever reviewed
	 * against the patterns they belong to. Recalculating is the only thing that makes the two agree.
	 */
	const dirtyJobs = result ? editedJobNames(result.shiftHours, shiftHoursEdits) : [];

	/** Nurse id -> name, so every approval outcome can be reported as a person rather than an id. */
	const nameByNurseId = useMemo(() => {
		const map = new Map<number, string>();
		for (const bucket of BUCKET_ORDER) for (const candidate of result ? rowsForBucket(result, bucket) : []) map.set(candidate.nurseId, candidate.nurseName);
		return map;
	}, [result]);
	const { namedOutcomes, accountingWarning } = useMemo(() => summariseApprovalOutcomes(creation, nameByNurseId), [creation, nameByNurseId]);
	const createdOutcomes = namedOutcomes.filter((outcome) => outcome.outcome === "created");
	const alreadyCurrentOutcomes = namedOutcomes.filter((outcome) => outcome.outcome === "alreadyCurrent");
	const failedOutcomes = namedOutcomes.filter((outcome) => outcome.outcome === "failed");

	const handleApprove = async () => {
		if (!departmentId || !result || dirtyJobs.length > 0) return;
		const created = await approve({ departmentId, settings: settings, approvedCandidateIds: approvedIds });
		setCreation(created);
		setConfirmOpen(false);
		showSnackbar?.(t("onboarding_load.recurring_approval_complete"), "success");
	};

	const tabs: SystemTab<EmployeeBucket>[] = BUCKET_ORDER.map((bucket) => ({
		label: BUCKET_LABELS[bucket],
		value: bucket,
		badge: result
			? { content: rowsForBucket(result, bucket).length, color: bucket === "ready" ? "success" : bucket === "needsReview" ? "warning" : "info" }
			: undefined,
	}));

	if (!departmentId) {
		return <EmptyState icon={<EventRepeatIcon />} title="Select a department" hint="Recurring patterns are calculated per department." />;
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }} data-testid="calculate-recurring-section">
			<RecurringSettingsPanel settings={settings} onChange={setSettings} jobNames={jobNames} disabled={calculating || approving} />

			<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
				<SystemButton
					buttonVariant="primary"
					loading={calculating}
					disabled={calculating || approving || hoursSave.saving}
					onClick={() => runCalculation(settings)}
					data-testid="recurring-calculate-button"
				>
					{result ? "Recalculate" : "Calculate patterns"}
				</SystemButton>
				<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>Reads history and proposes patterns. Writes nothing.</Typography>
			</Box>

			{!result && !calculating && (
				<EmptyState
					icon={<EventRepeatIcon />}
					title="No patterns proposed yet"
					hint="Choose your settings above and run the calculation. You will be able to review every proposal before anything is created."
					dataTestId="recurring-no-result"
				/>
			)}

			{result && (
				<>
					<RecurringResultSummary headline={result.headline} minimumScore={settings.minimumScore} />

					<RecurringShiftHoursPanel
						derived={result.shiftHours}
						edits={shiftHoursEdits}
						onEditsChange={(next) => {
							hoursSave.followEdits(shiftHoursEdits, next);
							setShiftHoursEdits(next);
						}}
						onRecalculate={() => runCalculation(settings)}
						saveToDepartment={hoursSave.panelControls}
						busy={calculating}
					/>
					{hoursSave.saveResult && <RecurringShiftHoursSaveResult result={hoursSave.saveResult} />}

					<Box>
						<SystemTabs tabs={tabs} currentTab={tab} onTabChange={setTab} dataTestId="recurring-bucket-tabs" sx={{ mb: 1 }} />
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, mb: 1.5 }}>{bucketDescription(tab, t)}</Typography>
						<RecurringCandidatesTable
							key={tab}
							bucket={tab}
							departmentId={departmentId}
							windowMonths={settings.baseWindowMonths}
							rows={rowsForBucket(result, tab)}
							minimumScore={settings.minimumScore}
							{...(tab === "skipped"
								? {}
								: {
										selectedIds: selectedIds[tab],
										onSelectionChange: (ids: string[]) => setSelectedIds((prev) => ({ ...prev, [tab]: ids })),
									})}
						/>
					</Box>

					{dirtyJobs.length > 0 && (
						<InlineHint tone="warning" dataTestId="recurring-dirty-hours-warning">
							You corrected the hours for {dirtyJobs.join(", ")} but haven't recalculated. The proposals below were fitted against the old hours, so recalculate
							before approving.
						</InlineHint>
					)}

					<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
						<SystemButton
							buttonVariant="primary"
							disabled={approvedIds.length === 0 || approving || calculating || dirtyJobs.length > 0}
							loading={approving}
							onClick={() => setConfirmOpen(true)}
							data-testid="recurring-approve-button"
						>
							{`Create templates for ${approvedIds.length} employee${approvedIds.length === 1 ? "" : "s"}`}
						</SystemButton>
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>
							{selectedIds.ready.length} from Ready · {selectedIds.needsReview.length} from Needs review
						</Typography>
					</Box>

					{creation && (
						<SystemBanner
							tone={failedOutcomes.length > 0 || creation.unmappedRoles.length > 0 || accountingWarning !== null ? "warning" : "success"}
							title={
								failedOutcomes.length > 0
									? t("onboarding_load.recurring_approval_partial")
									: createdOutcomes.length > 0
										? t("onboarding_load.recurring_approval_created")
										: t("onboarding_load.recurring_approval_already_current")
							}
							descriptionComponent="div"
							dataTestId="recurring-creation-result"
							description={
								<Box component="ul" sx={{ m: 0, pl: 2.5 }}>
									<li>{t("onboarding_load.recurring_approval_selected", { count: creation.approvedCount })}</li>
									{createdOutcomes.length > 0 && <li>{t("onboarding_load.recurring_approval_created_sets", { count: createdOutcomes.length })}</li>}
									{alreadyCurrentOutcomes.length > 0 && (
										<li>{t("onboarding_load.recurring_approval_already_current_sets", { count: alreadyCurrentOutcomes.length })}</li>
									)}
									{creation.retiredPatternCount > 0 && (
										<li data-testid="recurring-creation-retired">
											{t("onboarding_load.recurring_approval_retired_patterns", { count: creation.retiredPatternCount })}
										</li>
									)}
									{failedOutcomes.length > 0 && (
										<li data-testid="recurring-creation-failures">
											{t("onboarding_load.recurring_approval_failed_sets", { count: failedOutcomes.length })}
											<Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
												{failedOutcomes.map((outcome) => (
													<li key={outcome.candidateId} data-testid={`recurring-creation-failure-${outcome.candidateId}`}>
														<strong>{outcome.nurseName}:</strong> {outcome.reason}
													</li>
												))}
											</Box>
										</li>
									)}
									{creation.unmappedRoles.length > 0 && (
										<li>{t("onboarding_load.recurring_approval_unmapped_roles", { count: creation.unmappedRoles.length })}</li>
									)}
									{/* The result did not add up. Shown rather than thrown: the write has already
									    committed, so losing the panel would lose the only record of it. */}
									{accountingWarning !== null && (
										<li data-testid="recurring-creation-accounting-warning">
											<strong>This result is inconsistent and should be reported:</strong> {accountingWarning}. Check the schedule directly before re-running.
										</li>
									)}
								</Box>
							}
						/>
					)}
				</>
			)}

			<RecurringShiftHoursSaveDialog
				open={hoursSave.confirmOpen}
				onClose={hoursSave.closeConfirm}
				onConfirm={hoursSave.confirmSave}
				jobs={hoursSave.dialogJobs}
				saving={hoursSave.saving}
			/>

			<SystemDialog
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				title={t("onboarding_load.recurring_approval_confirm_title")}
				maxWidth="sm"
				fullWidth
				confirmButtonVariant="primary"
				confirm={t("onboarding_load.recurring_approval_confirm_action", { count: approvedIds.length })}
				confirmLoading={approving}
				onConfirm={handleApprove}
				dataTestId="recurring-approve-dialog"
				body={
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>
							{t("onboarding_load.recurring_approval_confirm_description", {
								count: approvedIds.length,
								ready: selectedIds.ready.length,
								needsReview: selectedIds.needsReview.length,
							})}
						</Typography>
						{selectedIds.needsReview.length > 0 && (
							<InlineHint tone="warning">{t("onboarding_load.recurring_approval_needs_review", { count: selectedIds.needsReview.length })}</InlineHint>
						)}
						<InlineHint tone="warning">{t("onboarding_load.recurring_approval_replacement_warning")}</InlineHint>
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{t("onboarding_load.recurring_approval_idempotency_note")}</Typography>
					</Box>
				}
			/>
		</Box>
	);
}
